import React, { useContext, useEffect, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import Lottie from "react-lottie";
import { Slide, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import DangerousIcon from "@mui/icons-material/Dangerous";
import "./LoginStyles.css";
import LoadingAnimation from "./loader.json";
import { UserContext } from "../../context/UserContext";
import { version } from "../../util/Version";
import BASE_URL from "../../config/apiConfig";
import { showErrorToast } from "../../util/showToast";

const Login = () => {
    const { user, login, loginSSO } = useContext(UserContext);
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const success = searchParams.get("success");

    const [usernameValue, setUsernameValue] = useState("");
    const [passwordValue, setPasswordValue] = useState("");
    const [loadingLogin, setLoadingLogin] = useState(false);
    const [loadingSSO, setLoadingSSO] = useState(false);
    const [showPassword, setShowPassword] = useState(false);

    useEffect(() => {
        document.title = "Login";
    }, []);

    useEffect(() => {
        const from = location.state?.from?.pathname;
        const save = sessionStorage.getItem("postLoginRedirect");
        if ((from && !save) || (from && save && from !== save)) {
            sessionStorage.setItem("postLoginRedirect", from);
        }
    }, [location.state]);

    useEffect(() => {
        if (success) {
            setLoadingSSO(true);
            loginSSO(setLoadingSSO)
        }
    }, [success]);

    useEffect(() => {
        if (user) {
            const redirectPath = sessionStorage.getItem("postLoginRedirect") || "/xplans";
            sessionStorage.removeItem("postLoginRedirect");
            navigate(redirectPath, { replace: true });
        }
    }, [user]);

    const handleSSOLogin = () => {
        const from = location.state?.from?.pathname;
        const save = sessionStorage.getItem("postLoginRedirect");
        if ((from && !save) || (from && save && from !== save)) {
            sessionStorage.setItem("postLoginRedirect", from);
        }
        setLoadingSSO(true);
        window.location.href = `${BASE_URL}/api/sso/start`;
    };

    const handleLogin = async (e) => {
        e.preventDefault();
        if (!usernameValue || !passwordValue) {
            showErrorToast("Please enter both username and password.", <DangerousIcon style={{ color: "#ef4337" }} />);
            return;
        }
        await login(usernameValue, passwordValue, setLoadingLogin);
    };

    const handleKeyPress = (e) => {
        if (e.key === "Enter") {
            handleLogin(e);
        }
    };

    const togglePassword = () => setShowPassword(!showPassword);

    const loaderLottie = {
        loop: true,
        autoplay: true,
        animationData: LoadingAnimation,
        rendererSettings: {
            preserveAspectRatio: "xMidYMid slice",
        },
    };

    return (
        <section className="itn-signin-page">
            <div className="itn-signin-window">
                <div className="itn-signin-header">
                    <img
                        src="/signin-icons/itn-logo-dark.png"
                        className="itn-signin-logo"
                        alt="ITN Logo"
                    />
                    <p className="itn-signin-title">ExPlan {version.replace(/\.\d+$/, "")}</p>
                </div>
                <div className="itn-signin-buttons">
                    <div
                        className="itn-signin-with-itn"
                        onClick={handleSSOLogin}
                    >
                        {loadingSSO ? (
                            <div className="itn-signin-animation">
                                <Lottie options={loaderLottie} width={40} />
                            </div>
                        ) : (
                            <>
                                <img
                                    src="/signin-icons/itn-icon-white.png"
                                    alt="ITN Logo"
                                    className="itn-signin-with-itn-icon"
                                />
                                Sign in with ITN Credentials
                            </>
                        )}
                    </div>
                </div>

                <div className="itn-signin-divider">
                    <p className="itn-signin-divider-text">or</p>
                </div>

                <div className="itn-signin-content">
                    <div className="itn-signin-content-header">
                        <h1 className="itn-signin-subtitle">Sign In</h1>
                    </div>

                    <form onSubmit={handleLogin} className="itn-signin-form">
                        <input
                            id="username"
                            className="itn-signin-field"
                            value={usernameValue}
                            onChange={(e) => setUsernameValue(e.target.value)}
                            onKeyDown={handleKeyPress}
                            placeholder="Username"
                            type="username"
                        />

                        <div className="itn-signin-password-field">
                            <input
                                id="password"
                                className="itn-signin-field"
                                value={passwordValue}
                                onChange={(e) => setPasswordValue(e.target.value)}
                                onKeyDown={handleKeyPress}
                                placeholder="Password"
                                type={showPassword ? "text" : "password"}
                            />
                            <img
                                src={showPassword ? "/signin-icons/close-eye-icon.png" : "/signin-icons/open-eye-icon.png"}
                                alt="Vie password icon"
                                className="itn-signin-password-icon"
                                onClick={togglePassword}
                            />
                        </div>

                        <button type="submit" className="itn-signin-submit">
                            {loadingLogin ? (
                                <div className="itn-signin-animation">
                                    <Lottie options={loaderLottie} width={40} />
                                </div>
                            ) : (
                                <span>Sign In</span>
                            )}
                        </button>
                    </form>
                </div>
            </div>
            <a
                href="https://www.immunetolerance.org/"
                target="_blank"
                rel="noreferrer"
                className="itn-copyright"
            >
                &#169;
                <img
                    src="/signin-icons/itn-icon-dark.png"
                    alt="ITN dark icon"
                    className="itn-copyright-icon"
                />
                Immune Tolerance Network
            </a>
            <ToastContainer
                position="top-center"
                autoClose={3000}
                hideProgressBar
                newestOnTop
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="light"
                transition={Slide}
            />
        </section>
    );
}

export default Login;